import React, { useState, useEffect, useRef } from "react";
import { formatPartialNational } from "../../utils/phoneFormat";
import PhoneInput from "../ui/PhoneInput";
import { useTheme } from "../../context/ThemeContext";
import { getInputStyles } from "../../utils/themeUtils";
import { Send } from "lucide-react";
import type { AnonChatFieldRequest } from "../../api/publicAiChatAPI";
import AnonChatSigninField from "./AnonChatSigninField";

/**
 * Inline structured input rendered below an assistant message when the
 * backend signals a `pending_field`. Replaces free-form natural-language
 * extraction (which fails on names like "Daniel Herbert Kemper II",
 * email typos, phone formats, etc.) with a real <input> the visitor
 * fills directly. Server validates + applies deterministically.
 *
 * Visitor can still ignore this field and type into the main textarea
 * if they prefer free text — both paths work, the field path is just
 * more reliable.
 */
interface Props {
  field: AnonChatFieldRequest;
  accentColor: string;
  disabled?: boolean;
  errorMessage?: string | null;
  onSubmit: (value: string) => void;
  /** Called once the inline sign-in form succeeds — chat then sends
   *  a continuation turn carrying the new auth token. */
  onAuthSuccess?: () => void;
}

const AnonChatStructuredField: React.FC<Props> = ({
  field,
  accentColor,
  disabled,
  errorMessage,
  onSubmit,
  onAuthSuccess,
}) => {
  const { isDarkMode } = useTheme();
  const [value, setValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  // Reset on field change so the visitor doesn't see stale text from
  // a prior turn's field.
  useEffect(() => {
    setValue("");
    // Focus when a new field appears — keeps the conversation flowing.
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [field.name]);

  // Inline sign-in form when the captured email belongs to an
  // existing member. Handles auth itself, then signals the chat to
  // continue with the new token. Branched AFTER all hooks so the
  // hook order stays stable across renders (rules-of-hooks).
  if (field.type === "signin") {
    return (
      <AnonChatSigninField
        emailPrefill={field.email_prefill}
        accentColor={accentColor}
        disabled={disabled}
        onAuthSuccess={() => onAuthSuccess && onAuthSuccess()}
      />
    );
  }

  const handleSubmit = () => {
    const trimmed = (value || "").trim();
    if (!trimmed && field.required !== false) return;
    onSubmit(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const inputClass = `flex-1 p-2 rounded border text-sm ${getInputStyles(isDarkMode)}`;

  let inputEl: React.ReactNode = null;
  if (field.type === "email") {
    inputEl = (
      <input aria-label={field.label || "Email"}
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="email"
        autoComplete="email"
        inputMode="email"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={field.placeholder || "name@example.com"}
        disabled={disabled}
        className={inputClass}
      />
    );
  } else if (field.type === "phone") {
    inputEl = (
      <PhoneInput
        ref={inputRef as React.RefObject<HTMLInputElement>}
        value={value}
        onChange={(d) => setValue(formatPartialNational(d))}
        onKeyDown={handleKeyDown}
        placeholder={field.placeholder || "(555) 123-4567"}
        disabled={disabled}
        className={inputClass}
      />
    );
  } else if (field.type === "date") {
    inputEl = (
      <input aria-label={field.label || "Date"}
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="date" autoComplete="off"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={inputClass}
      />
    );
  } else if (field.type === "select") {
    const options = field.options || [];
    inputEl = (
      <select
        ref={inputRef as React.RefObject<HTMLSelectElement>}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        className={inputClass}
      >
        <option value="">Choose…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt.replace(/_/g, " ")}
          </option>
        ))}
      </select>
    );
  } else if (field.type === "yesno") {
    return (
      <div className="px-3 pt-2 pb-1">
        <p className={`text-xs mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          {field.label}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onSubmit("yes")}
            disabled={disabled}
            className="px-4 py-1.5 rounded text-sm text-[var(--cc-action-text)] disabled:opacity-50"
            style={{ backgroundColor: accentColor }}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => onSubmit("no")}
            disabled={disabled}
            className={`px-4 py-1.5 rounded text-sm border ${
              isDarkMode
                ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            } disabled:opacity-50`}
          >
            No
          </button>
        </div>
        {errorMessage && (
          <p className="mt-1 text-xs text-red-500">{errorMessage}</p>
        )}
      </div>
    );
  } else {
    // Default: text input
    inputEl = (
      <input aria-label={field.label || "Answer"}
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        autoComplete={
          field.name === "first_name"
            ? "given-name"
            : field.name === "last_name"
            ? "family-name"
            : field.name === "street_address"
            ? "street-address"
            : field.name === "city"
            ? "address-level2"
            : field.name === "state"
            ? "address-level1"
            : field.name === "zip"
            ? "postal-code"
            : "off"
        }
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={field.placeholder || ""}
        disabled={disabled}
        className={inputClass}
      />
    );
  }

  return (
    <div className="px-3 pt-2 pb-1">
      <label
        className={`block text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {field.label}
      </label>
      <div className="flex items-center gap-2">
        {inputEl}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || (!value.trim() && field.required !== false)}
          className="p-2 rounded text-[var(--cc-action-text)] disabled:opacity-50"
          style={{ backgroundColor: accentColor }}
          aria-label={`Submit ${field.label}`}
        >
          <Send size={14} />
        </button>
      </div>
      {errorMessage && (
        <p className="mt-1 text-xs text-red-500">{errorMessage}</p>
      )}
    </div>
  );
};

export default AnonChatStructuredField;
